import React, { useState, useMemo, useEffect } from 'react'
import {
  Breadcrumbs,
  Button,
  Modal,
  Search,
  type BreadcrumbItem,
} from '@enamelai/design-system'
import type { FolderItem } from '@components/pages/Files/FilesAndLettersFolderGrid/FilesAndLettersFolderGrid.component'
import './MoveFileModal.css'

export const ROOT_FOLDER_ID = 'root'

const ALREADY_HERE_TITLE = 'Items are already in this location'

export interface MoveFileModalProps {
  show: boolean
  onHide: () => void
  itemNames: string[]
  defaultFolders: FolderItem[]
  customFolders: FolderItem[]
  onMove: (targetFolderId: string) => void
  onNewFolder?: (
    parentFolderId: string | null,
    parentFolderPath?: string,
  ) => void
  excludeFolderIds?: string[]
  showMainFolder?: boolean
  /** Targets where "Move here" / "Move in current folder" must be disabled (e.g. item's current folder, or a folder being moved onto itself). Use `root` for Main when files already live at workspace root. */
  blockedMoveTargetIds?: string[]
  renamingFolderId?: string | null
  onRenameFolderConfirm?: (folderId: string, newName: string) => void
  onRenameFolderCancel?: () => void
  /** Workspace folder_id for API: `root` or the id of the folder whose children are listed. */
  foldersLoading?: boolean
  onBrowseScopeChange?: (scopeFolderId: string) => void
}

const MoveFileModal: React.FC<MoveFileModalProps> = ({
  show,
  onHide,
  defaultFolders,
  customFolders,
  onMove,
  onNewFolder,
  excludeFolderIds = [],
  showMainFolder = true,
  blockedMoveTargetIds = [],
  renamingFolderId = null,
  onRenameFolderConfirm,
  onRenameFolderCancel,
  foldersLoading = false,
  onBrowseScopeChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [breadcrumb, setBreadcrumb] = useState<FolderItem[]>([])
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const currentFolder =
    breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1] : null

  const allFolders = useMemo(
    () => [...defaultFolders, ...customFolders],
    [defaultFolders, customFolders],
  )

  useEffect(() => {
    if (!show) return
    setBreadcrumb((prev) =>
      prev.map((crumb) => {
        const updated = allFolders.find((f) => f.id === crumb.id)
        if (!updated) return crumb
        if (
          updated.name === crumb.name &&
          updated.path === crumb.path &&
          updated.isDefault === crumb.isDefault
        ) {
          return crumb
        }
        return { ...crumb, ...updated }
      }),
    )
  }, [show, allFolders])

  useEffect(() => {
    if (!renamingFolderId) {
      setRenameValue('')
      return
    }
    const folder = allFolders.find((f) => f.id === renamingFolderId)
    setRenameValue(folder?.name ?? '')
  }, [renamingFolderId, allFolders])

  const excludedSet = useMemo(
    () => new Set(excludeFolderIds),
    [excludeFolderIds],
  )

  const blockedMoveTargetSet = useMemo(
    () => new Set(blockedMoveTargetIds),
    [blockedMoveTargetIds],
  )

  const isMoveTargetBlocked = (folderId: string): boolean =>
    blockedMoveTargetSet.has(folderId)

  const browseScopeFolderId = useMemo(
    () =>
      breadcrumb.length === 0
        ? ROOT_FOLDER_ID
        : breadcrumb[breadcrumb.length - 1].id,
    [breadcrumb],
  )

  useEffect(() => {
    if (!show || !onBrowseScopeChange) return
    onBrowseScopeChange(browseScopeFolderId)
  }, [show, browseScopeFolderId, onBrowseScopeChange])

  const visibleFolders = useMemo(() => {
    const term = searchTerm.toLowerCase().trim()
    const scoped = allFolders.filter(
      (f) =>
        !excludedSet.has(f.id) &&
        (currentFolder ? f.parentId === currentFolder.id : !f.parentId),
    )
    if (!term) return scoped
    return scoped.filter((f) => f.name.toLowerCase().includes(term))
  }, [allFolders, currentFolder, searchTerm, excludedSet])

  const handleOpen = (folder: FolderItem): void => {
    setBreadcrumb((prev) => [...prev, folder])
    setSearchTerm('')
    setHoveredId(null)
    setSelectedId(null)
  }

  const handleClose = (): void => {
    setBreadcrumb([])
    setSearchTerm('')
    setHoveredId(null)
    setSelectedId(null)
    if (renamingFolderId && onRenameFolderCancel) {
      onRenameFolderCancel()
    }
    onHide()
  }

  const handleBreadcrumbNavigate = (depth: number): void => {
    setBreadcrumb((prev) => prev.slice(0, depth))
    setSearchTerm('')
    setSelectedId(null)
  }

  const moveTargetId = selectedId ?? currentFolder?.id ?? null

  const handleMove = (): void => {
    if (!moveTargetId) return
    onMove(moveTargetId)
    handleClose()
  }

  const commitRename = (): void => {
    if (!renamingFolderId) return
    const trimmed = renameValue.trim()
    if (!trimmed) {
      onRenameFolderCancel?.()
      return
    }
    onRenameFolderConfirm?.(renamingFolderId, trimmed)
  }

  const handleRenameKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitRename()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      onRenameFolderCancel?.()
    }
  }

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      id: ROOT_FOLDER_ID,
      label: 'Folders',
      onClick: () => handleBreadcrumbNavigate(0),
    },
    ...breadcrumb.map((folder, idx) => ({
      id: folder.id,
      label: folder.name,
      onClick: () => handleBreadcrumbNavigate(idx + 1),
    })),
  ]

  const moveTargetBlocked = moveTargetId
    ? isMoveTargetBlocked(moveTargetId)
    : false

  const renderRowEnd = (
    folderId: string,
    folder?: FolderItem,
  ): React.ReactNode => {
    const isSelected = selectedId === folderId
    const canOpen =
      !!folder &&
      hoveredId === folderId &&
      renamingFolderId !== folderId &&
      !folder.isLocalDraft
    if (!isSelected && !canOpen) return null
    return (
      <div className="move-modal-folder-row-actions">
        {isSelected && (
          <span className="move-modal-folder-selected-label">
            {isMoveTargetBlocked(folderId) ? ALREADY_HERE_TITLE : 'Selected'}
          </span>
        )}
        {canOpen && folder && (
          <Button
            variant="primary"
            size="xs"
            onClick={(e) => {
              e.stopPropagation()
              handleOpen(folder)
            }}
          >
            Open
          </Button>
        )}
      </div>
    )
  }

  const getRowProps = (
    folderId: string,
    folder?: FolderItem,
  ): React.HTMLAttributes<HTMLDivElement> => ({
    role: 'option',
    'aria-selected': selectedId === folderId,
    tabIndex: 0,
    onMouseEnter: () => setHoveredId(folderId),
    onMouseLeave: () => setHoveredId(null),
    onClick: () => setSelectedId(folderId),
    onDoubleClick: folder ? () => handleOpen(folder) : undefined,
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setSelectedId(folderId)
      }
    },
  })

  const rowClassName = (folderId: string, extra = ''): string =>
    `move-modal-folder-row${extra}${selectedId === folderId ? ' selected' : ''}`

  const renderFolderList = (): React.ReactNode => {
    if (foldersLoading) {
      return <div className="move-modal-folder-empty">Loading folders…</div>
    }
    if (visibleFolders.length === 0) {
      return currentFolder ? (
        <div className="move-modal-folder-empty">
          <i className="mgc mgc_folder_open_line" aria-hidden />
          <span>This folder is currently open</span>
        </div>
      ) : (
        <div className="move-modal-folder-empty">No folders found</div>
      )
    }
    return visibleFolders.map((folder) => (
      <div
        key={folder.id}
        className={rowClassName(folder.id)}
        {...getRowProps(folder.id, folder)}
      >
        <div className="move-modal-folder-row-left">
          <i
            className={`mgc mgc_folder_fill ${folder.isDefault ? 'move-modal-folder-icon-default' : 'move-modal-folder-icon-custom'}`}
            aria-hidden
          />
          {renamingFolderId === folder.id ? (
            <input
              className="move-modal-folder-rename-input"
              type="text"
              aria-label="Folder name"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={handleRenameKeyDown}
              onBlur={commitRename}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          ) : (
            <span className="move-modal-folder-name">{folder.name}</span>
          )}
        </div>
        {renderRowEnd(folder.id, folder)}
      </div>
    ))
  }

  return (
    <Modal
      open={show}
      onClose={handleClose}
      size="sm"
      title="Move files"
      className="move-file-modal"
      bodyClassName="move-modal-body"
      footerStart={
        <Button
          variant="tertiary"
          size="sm"
          onClick={() =>
            onNewFolder?.(currentFolder?.id ?? null, currentFolder?.path)
          }
        >
          New folder
        </Button>
      }
      secondaryLabel="Cancel"
      onSecondaryClick={handleClose}
      primaryLabel="Move"
      primaryDisabled={!moveTargetId || moveTargetBlocked}
      onPrimaryClick={handleMove}
    >
      {currentFolder && (
        <Breadcrumbs
          items={breadcrumbItems}
          showBackIcon={false}
          className="move-modal-breadcrumb"
        />
      )}

      {!currentFolder && (
        <Search
          value={searchTerm}
          placeholder="Search folder"
          ariaLabel="Search folder"
          onChange={setSearchTerm}
          onClear={() => setSearchTerm('')}
          className="move-modal-search"
        />
      )}

      <div
        className="move-modal-folder-list"
        role="listbox"
        aria-label="Destination folder"
      >
        {!currentFolder && showMainFolder && (
          <div
            className={rowClassName(
              ROOT_FOLDER_ID,
              ' move-modal-folder-row-main',
            )}
            {...getRowProps(ROOT_FOLDER_ID)}
          >
            <div className="move-modal-folder-row-left">
              <i
                className="mgc mgc_folder_fill move-modal-folder-icon-default"
                aria-hidden
              />
              <span className="move-modal-folder-name">Main</span>
            </div>
            {renderRowEnd(ROOT_FOLDER_ID)}
          </div>
        )}
        {renderFolderList()}
      </div>
    </Modal>
  )
}

export default MoveFileModal
